'use client';

import { useEffect, useMemo, useState } from 'react';
import styles from '@/styles/dashboard.module.css';
import { useAuth } from '@/lib/context/AuthContext';
import { DownloadIcon, SettingsIcon, EditIcon } from './Icons';
import { updateProfile } from '@/lib/api/auth';
import toast from 'react-hot-toast';

export function ProfileDropdown({ onSettings }: { onSettings: () => void }) {
  const { user, signOut, token, setSession } = useAuth();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  // FIX: Preview pakai URL avatar yang benar dari response API
  const avatarUrl = (user as any)?.avatar || (user as any)?.photo_url || (user as any)?.profile_picture || (user as any)?.image || '';
  const [avatarPreview, setAvatarPreview] = useState<string>(avatarUrl);
  const [saving, setSaving] = useState(false);

  const isElectron = useMemo(() => {
    try {
      return !!(typeof window !== 'undefined' && (window as any).electronAPI?.isPackaged?.());
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    // FIX: Modal bisa dibuka ulang setelah user data load, jadi sync preview dari profil existing
    setAvatarPreview(avatarUrl);
  }, [avatarUrl]);

  const openProfile = () => {
    setName(user?.name || '');
    setAvatarFile(null);
    // FIX: Inisialisasi preview dari foto existing saat modal dibuka
    setAvatarPreview(avatarUrl);
    setShowProfileModal(true);
  };

  const onPickAvatar: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.[0] || null;
    setAvatarFile(file);
    if (file) {
      const url = URL.createObjectURL(file);
      setAvatarPreview(url);
    }
  };

  const onSaveProfile = async () => {
    if (!token) return toast.error('Session expired, please login again');
    if (!name.trim()) return toast.error('Name is required');

    setSaving(true);
    try {
      let nextUser = { ...(user || {}), name: name.trim(), avatar: avatarPreview } as any;
      try {
        const updated = await updateProfile({ name: name.trim(), avatar: avatarFile }, token || undefined);
        nextUser = { ...nextUser, ...updated };
      } catch {
      }

      setSession(token, nextUser);
      toast.success('Profile updated');
      setShowProfileModal(false);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className={styles.profileDropdown}>
        <div className={styles.profileDropdownHeader}>
          <span className={styles.profileName}>{user?.name || 'Guest'}</span>
          <span className={styles.profileEmail}>{user?.email || 'guest@example.com'}</span>
        </div>

        <div className={styles.profileDropdownDivider} />

        <button type="button" className={styles.profileDropdownItem} onClick={onSettings}>
          <SettingsIcon size={15} color="currentColor" />
          Settings
        </button>

        <div className={styles.profileDropdownDivider} />

        <button type="button" className={styles.profileDropdownItem} onClick={openProfile}>
          <EditIcon size={14} color="currentColor" />
          My Profile
        </button>
        <button type="button" className={styles.profileDropdownItem} onClick={() => { window.location.href = '/more'; }}>
          Help
        </button>

        <div className={styles.profileDropdownDivider} />

        <button type="button" className={`${styles.profileDropdownItem} ${styles.profileDropdownItemDanger}`} onClick={signOut}>
          Sign Out
        </button>

        {!isElectron && (
          <>
            <div className={styles.profileDropdownDivider} />
            <button 
                  type="button" 
                  className={`${styles.profileDropdownItem} ${styles.profileDropdownDownload}`}
                  onClick={() => {
                    window.open("https://drive.satkomlek-tni.mil.id/browser/installer", "_blank", "noopener,noreferrer");
                  }}
                >
                  <DownloadIcon size={14} color="currentColor" />
                  Download the MeetX app
                </button>
          </>
        )}
      </div>

      {showProfileModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'grid', placeItems: 'center', zIndex: 9999 }}>
          <div style={{ width: 420, maxWidth: '90vw', background: '#111827', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12, padding: 18, color: '#fff' }}>
            <h3 style={{ margin: '0 0 14px', fontSize: 18 }}>My Profile</h3>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14 }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#374151', backgroundImage: avatarPreview ? `url(${avatarPreview})` : undefined, backgroundSize: 'cover', backgroundPosition: 'center' }} />
              <input type="file" accept="image/*" onChange={onPickAvatar} />
            </div>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8, border: '1px solid #374151', background: '#0b1220', color: '#fff' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button onClick={() => setShowProfileModal(false)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #4b5563', background: 'transparent', color: '#fff' }}>Cancel</button>
              <button onClick={onSaveProfile} disabled={saving} style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: '#FA5C5C', color: '#fff', fontWeight: 700 }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
